import React from 'react';
import { Globe2, Paintbrush, QrCode, Save, Store } from 'lucide-react';
import AdminShell from '../../admin/components/layout/AdminShell';
import AdminPageHeader from '../../admin/components/shared/AdminPageHeader';
import AdminPanel from '../../admin/components/shared/AdminPanel';
import SecondaryActionButton from '../../admin/components/buttons/SecondaryActionButton';
import TextField from '../../admin/components/fields/TextField';
import TextareaField from '../../admin/components/fields/TextareaField';
import ImageUploadField from '../../admin/components/fields/ImageUploadField';
import SmartImagePreview from '../../components/shared/SmartImagePreview';
import ArabAliLogo from '../../components/ArabAliLogo';
import { useAdminContext } from '../../context/AdminContext';

const backgroundOptions = [
  { value: 'soft-gradient', label: 'Soft gradient' },
  { value: 'solid', label: 'Solid color' },
  { value: 'photo-overlay', label: 'Photo overlay' },
] as const;

const buttonOptions = [
  { value: 'pill', label: 'Pill buttons' },
  { value: 'rounded', label: 'Rounded buttons' },
  { value: 'soft', label: 'Soft corners' },
] as const;

const ThemeColorField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
}> = ({ label, value, onChange }) => (
  <div className="rounded-[24px] border border-white/10 bg-[#120807] p-4">
    <div className="text-[11px] uppercase tracking-[0.24em] text-white/45">{label}</div>
    <div className="mt-3 flex items-center gap-3">
      <input
        type="color"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-12 w-14 cursor-pointer rounded-xl border border-white/10 bg-transparent p-1"
      />
      <input
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="flex-1 rounded-2xl border border-white/10 bg-[#1a0b08] px-4 py-3 text-white placeholder:text-white/35 focus:border-amber-400/35 focus:outline-none"
      />
    </div>
  </div>
);

const ChoiceGroup: React.FC<{
  label: string;
  value: string;
  options: readonly { value: string; label: string }[];
  onChange: (value: string) => void;
}> = ({ label, value, options, onChange }) => (
  <div>
    <div className="mb-3 text-[11px] uppercase tracking-[0.3em] text-amber-100/55">{label}</div>
    <div className="flex flex-wrap gap-2">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={`rounded-full border px-4 py-2 text-sm transition ${
            value === option.value
              ? 'border-amber-300/40 bg-amber-400/12 text-amber-100'
              : 'border-white/10 bg-white/5 text-white/65 hover:bg-white/8'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  </div>
);

const AdminSettingsPage: React.FC = () => {
  const { draftWorkspace, draftStatus, draftError, updateWorkspace, saveDraft, saving, uploadImage } =
    useAdminContext();

  const settings = draftWorkspace?.settings;
  const brandingAssets =
    draftWorkspace?.mediaAssets.filter((asset) => asset.kind !== 'item-image') ?? [];

  return (
    <AdminShell>
      <AdminPageHeader
        eyebrow="Restaurant Setup"
        title="Business info, branding, and website look"
        description="Everything here controls how guests see the live menu after you publish. Change the name, logo, colors, tab icon, QR link, and contact details without code."
        actions={
          <SecondaryActionButton onClick={() => void saveDraft()} disabled={saving || draftStatus !== 'ready'}>
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Draft'}
          </SecondaryActionButton>
        }
      />

      {draftError && (
        <AdminPanel className="p-4">
          <div className="rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm text-red-100">
            {draftError}
          </div>
        </AdminPanel>
      )}

      {!settings || draftStatus === 'loading' ? (
        <AdminPanel className="p-8 text-center text-white/60">Loading restaurant setup...</AdminPanel>
      ) : (
        <div className="space-y-6">
          <AdminPanel className="p-6">
            <div className="mb-5 flex items-center gap-3">
              <Store className="h-5 w-5 text-amber-300" />
              <div>
                <div className="text-[11px] uppercase tracking-[0.3em] text-amber-100/45">Business Info</div>
                <div className="mt-1 text-xl font-semibold text-white">Basic restaurant identity</div>
              </div>
            </div>

            <div className="grid gap-5 lg:grid-cols-2">
              <TextField
                label="Restaurant Name"
                value={settings.name}
                onChange={(value) =>
                  updateWorkspace((workspace) => ({
                    ...workspace,
                    settings: { ...workspace.settings, name: value },
                  }))
                }
                required
              />
              <TextField
                label="Subtitle"
                value={settings.subtitle}
                onChange={(value) =>
                  updateWorkspace((workspace) => ({
                    ...workspace,
                    settings: { ...workspace.settings, subtitle: value },
                  }))
                }
              />
              <TextField
                label="Phone Number"
                value={settings.phoneNumber}
                onChange={(value) =>
                  updateWorkspace((workspace) => ({
                    ...workspace,
                    settings: { ...workspace.settings, phoneNumber: value },
                  }))
                }
                required
              />
              <TextField
                label="Location"
                value={settings.location}
                onChange={(value) =>
                  updateWorkspace((workspace) => ({
                    ...workspace,
                    settings: { ...workspace.settings, location: value },
                  }))
                }
                required
              />
              <TextField
                label="Map Link"
                value={settings.mapsUrl}
                onChange={(value) =>
                  updateWorkspace((workspace) => ({
                    ...workspace,
                    settings: { ...workspace.settings, mapsUrl: value },
                  }))
                }
                helpText="Used when guests tap the location on the website."
              />
              <TextField
                label="Call Button Text"
                value={settings.callToActionLabel}
                onChange={(value) =>
                  updateWorkspace((workspace) => ({
                    ...workspace,
                    settings: { ...workspace.settings, callToActionLabel: value },
                  }))
                }
                helpText="Example: Call to Order, Call Reception, or Call Restaurant."
              />
              <TextField
                label="Currency Code"
                value={settings.currencyCode}
                onChange={(value) =>
                  updateWorkspace((workspace) => ({
                    ...workspace,
                    settings: { ...workspace.settings, currencyCode: value.toUpperCase() },
                  }))
                }
                helpText="Example: ETB, USD, AED."
              />
              <TextField
                label="Currency Label"
                value={settings.currencySymbol}
                onChange={(value) =>
                  updateWorkspace((workspace) => ({
                    ...workspace,
                    settings: { ...workspace.settings, currencySymbol: value },
                  }))
                }
                helpText="Shown beside prices on the public menu."
              />
              <div className="lg:col-span-2">
                <TextareaField
                  label="Homepage Intro"
                  value={settings.homepageIntro ?? ''}
                  onChange={(value) =>
                    updateWorkspace((workspace) => ({
                      ...workspace,
                      settings: { ...workspace.settings, homepageIntro: value },
                    }))
                  }
                  placeholder="Welcome guests and explain what they can do on the menu site."
                />
              </div>
            </div>
          </AdminPanel>

          <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
            <AdminPanel className="p-6">
              <div className="mb-5 flex items-center gap-3">
                <Paintbrush className="h-5 w-5 text-amber-300" />
                <div>
                  <div className="text-[11px] uppercase tracking-[0.3em] text-amber-100/45">Branding</div>
                  <div className="mt-1 text-xl font-semibold text-white">Logo, tab icon, and main images</div>
                </div>
              </div>

              <div className="space-y-5">
                <ImageUploadField
                  label="Main Cover Image"
                  value={settings.coverImage}
                  onChange={(value) =>
                    updateWorkspace((workspace) => ({
                      ...workspace,
                      settings: { ...workspace.settings, coverImage: value },
                    }))
                  }
                  assets={brandingAssets}
                  onUpload={(file) => uploadImage(file, 'restaurant-cover', `${settings.name} cover image`)}
                  helpText="Main hero photo for the public site and the QR poster."
                />

                <ImageUploadField
                  label="Logo Image"
                  value={settings.logoImage}
                  onChange={(value) =>
                    updateWorkspace((workspace) => ({
                      ...workspace,
                      settings: { ...workspace.settings, logoImage: value },
                    }))
                  }
                  assets={brandingAssets}
                  onUpload={(file) => uploadImage(file, 'branding-image', `${settings.name} logo`)}
                  helpText="Used in the header, QR page, and brand cards."
                />

                <ImageUploadField
                  label="Tab Icon / Favicon"
                  value={settings.faviconImage}
                  onChange={(value) =>
                    updateWorkspace((workspace) => ({
                      ...workspace,
                      settings: { ...workspace.settings, faviconImage: value },
                    }))
                  }
                  assets={brandingAssets}
                  onUpload={(file) => uploadImage(file, 'branding-image', `${settings.name} favicon`)}
                  helpText="Small icon shown in the browser tab on phones and desktop."
                />
              </div>
            </AdminPanel>

            <AdminPanel className="p-6">
              <div className="mb-5 flex items-center gap-3">
                <Globe2 className="h-5 w-5 text-amber-300" />
                <div>
                  <div className="text-[11px] uppercase tracking-[0.3em] text-amber-100/45">Website Look</div>
                  <div className="mt-1 text-xl font-semibold text-white">Theme colors and styles</div>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <ThemeColorField
                  label="Primary"
                  value={settings.brandColors.primary}
                  onChange={(value) =>
                    updateWorkspace((workspace) => ({
                      ...workspace,
                      settings: {
                        ...workspace.settings,
                        brandColors: { ...workspace.settings.brandColors, primary: value },
                      },
                    }))
                  }
                />
                <ThemeColorField
                  label="Accent"
                  value={settings.brandColors.accent}
                  onChange={(value) =>
                    updateWorkspace((workspace) => ({
                      ...workspace,
                      settings: {
                        ...workspace.settings,
                        brandColors: { ...workspace.settings.brandColors, accent: value },
                      },
                    }))
                  }
                />
                <ThemeColorField
                  label="Surface"
                  value={settings.brandColors.surface}
                  onChange={(value) =>
                    updateWorkspace((workspace) => ({
                      ...workspace,
                      settings: {
                        ...workspace.settings,
                        brandColors: { ...workspace.settings.brandColors, surface: value },
                      },
                    }))
                  }
                />
                <ThemeColorField
                  label="Background"
                  value={settings.brandColors.background}
                  onChange={(value) =>
                    updateWorkspace((workspace) => ({
                      ...workspace,
                      settings: {
                        ...workspace.settings,
                        brandColors: { ...workspace.settings.brandColors, background: value },
                      },
                    }))
                  }
                />
                <div className="sm:col-span-2">
                  <ThemeColorField
                    label="Text"
                    value={settings.brandColors.text}
                    onChange={(value) =>
                      updateWorkspace((workspace) => ({
                        ...workspace,
                        settings: {
                          ...workspace.settings,
                          brandColors: { ...workspace.settings.brandColors, text: value },
                        },
                      }))
                    }
                  />
                </div>
              </div>

              <div className="mt-5 space-y-5">
                <ChoiceGroup
                  label="Background Style"
                  value={settings.backgroundStyle}
                  options={backgroundOptions}
                  onChange={(value) =>
                    updateWorkspace((workspace) => ({
                      ...workspace,
                      settings: { ...workspace.settings, backgroundStyle: value as typeof settings.backgroundStyle },
                    }))
                  }
                />

                <ChoiceGroup
                  label="Button Shape"
                  value={settings.buttonStyle}
                  options={buttonOptions}
                  onChange={(value) =>
                    updateWorkspace((workspace) => ({
                      ...workspace,
                      settings: { ...workspace.settings, buttonStyle: value as typeof settings.buttonStyle },
                    }))
                  }
                />
              </div>
            </AdminPanel>
          </div>

          <AdminPanel className="p-6">
            <div className="mb-5 flex items-center gap-3">
              <QrCode className="h-5 w-5 text-amber-300" />
              <div>
                <div className="text-[11px] uppercase tracking-[0.3em] text-amber-100/45">QR and Sharing</div>
                <div className="mt-1 text-xl font-semibold text-white">Live menu link and guest preview</div>
              </div>
            </div>

            <div className="grid gap-5 xl:grid-cols-[1fr_1.1fr]">
              <div className="space-y-5">
                <TextField
                  label="Menu URL"
                  value={settings.menuUrl}
                  onChange={(value) =>
                    updateWorkspace((workspace) => ({
                      ...workspace,
                      settings: { ...workspace.settings, menuUrl: value },
                    }))
                  }
                  helpText="Used by the QR page and printed QR materials."
                />

                <div className="rounded-[28px] border border-white/10 bg-[#120807] p-5">
                  <div className="text-[11px] uppercase tracking-[0.24em] text-white/45">Launch checklist</div>
                  <div className="mt-4 grid gap-3 text-sm text-white/72 sm:grid-cols-2">
                    {[
                      ['Business name', Boolean(settings.name.trim())],
                      ['Phone number', Boolean(settings.phoneNumber.trim())],
                      ['Map link', Boolean(settings.mapsUrl.trim())],
                      ['Main cover image', Boolean(settings.coverImage.trim())],
                      ['Logo image', Boolean(settings.logoImage.trim())],
                      ['Tab icon', Boolean(settings.faviconImage.trim())],
                    ].map(([label, ready]) => (
                      <div key={`${label}`} className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                        <div className="font-medium text-white">{label}</div>
                        <div className={`mt-1 text-xs uppercase tracking-[0.2em] ${ready ? 'text-emerald-200' : 'text-amber-200'}`}>
                          {ready ? 'Ready' : 'Still needed'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div
                className="overflow-hidden rounded-[30px] border border-white/10"
                style={{
                  background: `linear-gradient(180deg, ${settings.brandColors.surface}, ${settings.brandColors.background})`,
                  color: settings.brandColors.text,
                }}
              >
                <div className="grid gap-0 lg:grid-cols-[260px_1fr]">
                  <SmartImagePreview src={settings.coverImage} alt={settings.name} className="h-full min-h-[260px] rounded-none border-0" fit="cover" />
                  <div className="p-5">
                    <div className="flex items-center justify-between gap-3">
                      <ArabAliLogo
                        compact
                        logoImage={settings.logoImage}
                        name={settings.name}
                      />
                      <div className="overflow-hidden rounded-full border border-white/10 bg-white/10 p-2">
                        <SmartImagePreview
                          src={settings.faviconImage || settings.logoImage}
                          alt={`${settings.name} tab icon`}
                          className="h-10 w-10 rounded-full border-0"
                          fit="cover"
                        />
                      </div>
                    </div>

                    <div className="mt-5 text-[11px] uppercase tracking-[0.35em]" style={{ color: `${settings.brandColors.primary}` }}>
                      Live Preview
                    </div>
                    <div className="mt-3 text-3xl font-semibold">{settings.name}</div>
                    <div className="mt-2 text-sm uppercase tracking-[0.28em]" style={{ color: `${settings.brandColors.primary}` }}>
                      {settings.subtitle || 'Restaurant menu'}
                    </div>
                    <div className="mt-4 text-sm leading-7" style={{ color: `${settings.brandColors.text}cc` }}>
                      {settings.homepageIntro || 'Add a welcome message for guests.'}
                    </div>
                    <div className="mt-5 grid gap-3 sm:grid-cols-2">
                      <div className="rounded-2xl border border-white/10 bg-black/15 px-4 py-3">
                        <div className="text-[11px] uppercase tracking-[0.24em] text-white/45">Location</div>
                        <div className="mt-1">{settings.location || 'Add location'}</div>
                      </div>
                      <div className="rounded-2xl border border-white/10 bg-black/15 px-4 py-3">
                        <div className="text-[11px] uppercase tracking-[0.24em] text-white/45">Phone</div>
                        <div className="mt-1">{settings.phoneNumber || 'Add phone number'}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="mt-5 inline-flex items-center justify-center px-5 py-3 text-sm font-semibold text-[#2b1209]"
                      style={{
                        borderRadius:
                          settings.buttonStyle === 'rounded'
                            ? '1.25rem'
                            : settings.buttonStyle === 'soft'
                              ? '1rem'
                              : '9999px',
                        background: `linear-gradient(135deg, ${settings.brandColors.primary}, ${settings.brandColors.accent})`,
                      }}
                    >
                      {settings.callToActionLabel}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </AdminPanel>
        </div>
      )}
    </AdminShell>
  );
};

export default AdminSettingsPage;
